import { englishContents, koreanContents } from "@/constants/language";
import { LanguageCode, PotooLandingContentsMap, PotooLandingContentsType } from "@/types/language";
import { LanguageMap } from "@potoo-modules/constants";
import { NextRouter, useRouter } from "next/router";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

type LanguageContextType = {
  language: LanguageCode;
  contents: PotooLandingContentsType;
  isKorean: boolean;
  setLanguage: Dispatch<SetStateAction<LanguageCode>>;
};

const LanguageContext = createContext<LanguageContextType>({
  language: LanguageMap.KOREAN,
  contents: koreanContents,
  isKorean: true,
  setLanguage: () => {},
});

export const LanguageContextProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguage] = useState<LanguageCode>(LanguageMap.KOREAN);
  const router = useRouter();

  const contentsMap: PotooLandingContentsMap = {
    [LanguageMap.KOREAN]: koreanContents,
    [LanguageMap.ENGLISH]: englishContents,
  };
  const contents = contentsMap[language];
  const isKorean = language === LanguageMap.KOREAN;

  const isEnglishOrKorean = (router: NextRouter) =>
    router.query?.language === LanguageMap.ENGLISH || router.query?.language === LanguageMap.KOREAN;

  useLayoutEffect(() => {
    const englishOrKorean = isEnglishOrKorean(router);
    if (router.isReady && englishOrKorean && language !== router.query?.language) {
      setLanguage(router.query?.language as LanguageCode);
    }
  }, [router.query?.language, router.isReady]);

  useEffect(() => {
    // 100ms 간격으로 window.ChannelIO가 로드되었는지 확인
    const intervalId = setInterval(() => {
      if (window?.ChannelIO) {
        clearInterval(intervalId);
        const englishOrKorean = isEnglishOrKorean(router);
        if (englishOrKorean && window.ChannelIO) {
          window.ChannelIO("updateUser", {
            language,
          });
        }
      }
    }, 100);
    return () => clearInterval(intervalId);
  }, [language, router]);

  return (
    <LanguageContext.Provider
      value={{
        language,
        contents,
        isKorean,
        setLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = () => useContext(LanguageContext);
